import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, ImageIcon } from "lucide-react";
import { BUSINESS } from "../constants/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("asc_admin_token") || "");
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/service-requests`, { headers: { "X-Admin-Token": t } });
      setRequests(res.data.requests);
      localStorage.setItem("asc_admin_token", t);
    } catch {
      setError("Invalid token or server error.");
      setRequests(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-24 py-16" data-testid="admin-page">
      <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#FF3B30] text-sm uppercase tracking-[0.15em]" data-testid="admin-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to Site
      </Link>
      <h1 className="mt-8 font-oswald font-bold uppercase tracking-tight text-4xl">Service Requests</h1>
      <p className="mt-2 text-zinc-500 text-sm">{BUSINESS.name} — internal view</p>

      {requests === null && (
        <form
          className="mt-10 max-w-md"
          onSubmit={(e) => { e.preventDefault(); load(token); }}
          data-testid="admin-login-form"
        >
          <label htmlFor="admin-token" className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Admin Token</label>
          <input
            id="admin-token"
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full bg-zinc-900/60 border border-zinc-800 px-4 py-3 text-sm focus:border-[#FF3B30] focus:outline-none"
            placeholder="Enter admin token"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-4 bg-[#FF3B30] px-8 py-3 font-oswald uppercase tracking-[0.15em] text-sm hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-60 flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} View Requests
          </button>
          {error && <p className="mt-3 text-sm text-[#FF3B30]" data-testid="admin-error">{error}</p>}
        </form>
      )}

      {requests !== null && (
        <div className="mt-10 space-y-6" data-testid="admin-requests-list">
          {requests.length === 0 && <p className="text-zinc-500">No service requests yet.</p>}
          {requests.map((r) => (
            <article key={r.id} className="border border-zinc-800 bg-zinc-900/40 p-6" data-testid={`admin-request-${r.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-oswald uppercase tracking-tight text-2xl">{r.name}</h2>
                <time className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleString()}</time>
              </div>
              <p className="mt-1 text-sm text-[#FF3B30]">{r.vehicle_year} {r.make} {r.model} — {r.engine}</p>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                <div><dt className="text-zinc-500 inline">Phone: </dt><dd className="inline"><a className="hover:text-[#FF3B30]" href={`tel:${r.phone}`}>{r.phone}</a></dd></div>
                <div><dt className="text-zinc-500 inline">Email: </dt><dd className="inline"><a className="hover:text-[#FF3B30]" href={`mailto:${r.email}`}>{r.email}</a></dd></div>
                <div><dt className="text-zinc-500 inline">Mileage: </dt><dd className="inline">{r.mileage}</dd></div>
                {r.vin && <div><dt className="text-zinc-500 inline">VIN: </dt><dd className="inline">{r.vin}</dd></div>}
              </dl>
              <p className="mt-4 text-sm text-zinc-300 border-l-2 border-[#FF3B30] pl-4">{r.description}</p>
              {r.photos?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {r.photos.map((p, i) => (
                    <a key={i} href={`${API}/files/${p.storage_path}?token=${encodeURIComponent(localStorage.getItem("asc_admin_token") || "")}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-photo-${r.id}-${i}`}>
                      <img
                        src={`${API}/files/${p.storage_path}?token=${encodeURIComponent(localStorage.getItem("asc_admin_token") || "")}`}
                        alt={p.original_filename || `Vehicle photo ${i + 1}`}
                        className="w-24 h-24 object-cover border border-zinc-700 hover:border-[#FF3B30] transition-colors duration-200"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
              {r.photo_count === 0 && (
                <p className="mt-4 text-xs text-zinc-600 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> No photos attached</p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
